/**
 * Magasin `shifts` : les quarts de caisse du poste (plan 1.2, PR 1.5b).
 *
 * Un quart s'ouvre avec son fond de caisse (design 4.2) et reste ouvert
 * jusqu'a la fermeture (PR 2.4). Un seul quart ouvert a la fois sur le
 * poste : son shift_id est garde dans `meta` (META_KEYS.OPEN_SHIFT), ecrit
 * dans la MEME transaction que le quart. Ouvrir deux fois (double clic, deux
 * onglets) rend le quart deja ouvert, sans en creer un second.
 *
 * Forme d'un enregistrement (cle : shift_id, UUID v4) :
 *   { shift_id, terminal_id, store_id, status: 'OPEN',
 *     opened_at (ISO 8601 local), opened_by: { employee_id, display_name },
 *     opening_float_cents, closed_at: null, closed_by: null }
 */
import { requestToPromise, transactionDone } from './idb';
import { META_KEYS, STORES } from './schema';

export const SHIFT_STATUS = Object.freeze({ OPEN: 'OPEN', CLOSED: 'CLOSED' });

/** Fond de caisse propose quand la configuration du magasin n'en donne pas : 200,00 $. */
export const FOND_PAR_DEFAUT_CENTS = 20000;

/** Plafond d'un fond de caisse saisi : 9 999,99 $. */
export const FOND_MAX_CENTS = 999_999;

export class ShiftError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'ShiftError';
        this.code = code;
    }
}

function deuxChiffres(n, longueur = 2) {
    return String(n).padStart(longueur, '0');
}

/**
 * Date en ISO 8601 avec le decalage local (« 2026-11-03T14:22:05.120-05:00 »),
 * comme `ts_local` du journal (plan 1.5).
 * @param {Date} date
 */
export function horodatageLocal(date) {
    const offset = -date.getTimezoneOffset();
    const signe = offset >= 0 ? '+' : '-';
    const abs = Math.abs(offset);
    return `${date.getFullYear()}-${deuxChiffres(date.getMonth() + 1)}-${deuxChiffres(date.getDate())}`
        + `T${deuxChiffres(date.getHours())}:${deuxChiffres(date.getMinutes())}:${deuxChiffres(date.getSeconds())}`
        + `.${deuxChiffres(date.getMilliseconds(), 3)}${signe}${deuxChiffres(Math.floor(abs / 60))}:${deuxChiffres(abs % 60)}`;
}

/**
 * Fond de caisse a proposer : celui de la configuration du magasin
 * (`default_opening_float_cents`, GET /caisse/config ou activation) s'il en
 * donne un, sinon 200,00 $.
 * @param {object|null} config
 */
export function fondPropose(config) {
    const v = config && config.default_opening_float_cents;
    return Number.isSafeInteger(v) && v >= 0 && v <= FOND_MAX_CENTS ? v : FOND_PAR_DEFAUT_CENTS;
}

/** Quart ouvert du poste, ou null. */
export async function getOpenShift(db) {
    const tx = db.transaction([STORES.META, STORES.SHIFTS], 'readonly');
    const pointer = await requestToPromise(tx.objectStore(STORES.META).get(META_KEYS.OPEN_SHIFT));
    if (!pointer || !pointer.value) return null;
    const shift = await requestToPromise(tx.objectStore(STORES.SHIFTS).get(pointer.value));
    return shift && shift.status === SHIFT_STATUS.OPEN ? shift : null;
}

/**
 * Ouvre le quart du poste, en UNE transaction (meta + shifts). Si un quart
 * est deja ouvert, il est rendu tel quel (created: false) : rien n'est ecrit.
 *
 * @param {IDBDatabase} db
 * @param {object} options
 * @param {{ employee_id: number, display_name: string }} options.employee employe connecte par NIP
 * @param {number} options.openingFloatCents fond de caisse, cents entiers
 * @param {() => Date} [options.now]
 * @param {() => string} [options.newId] UUID v4
 * @returns {Promise<{ shift: object, created: boolean }>}
 */
export async function openShift(db, { employee, openingFloatCents, now = () => new Date(), newId = () => globalThis.crypto.randomUUID() }) {
    if (!Number.isSafeInteger(openingFloatCents) || openingFloatCents < 0 || openingFloatCents > FOND_MAX_CENTS) {
        throw new ShiftError('Fond de caisse invalide', 'FOND_INVALIDE');
    }
    if (!employee || !Number.isSafeInteger(employee.employee_id)) {
        throw new ShiftError('Employe requis pour ouvrir la caisse', 'EMPLOYE_REQUIS');
    }

    const tx = db.transaction([STORES.META, STORES.SHIFTS], 'readwrite');
    const done = transactionDone(tx);
    const meta = tx.objectStore(STORES.META);
    const shifts = tx.objectStore(STORES.SHIFTS);
    try {
        const terminalRecord = await requestToPromise(meta.get(META_KEYS.TERMINAL));
        const terminal = terminalRecord && terminalRecord.value;
        if (!terminal) throw new ShiftError('Poste non active', 'POSTE_NON_ACTIVE');

        const pointer = await requestToPromise(meta.get(META_KEYS.OPEN_SHIFT));
        if (pointer && pointer.value) {
            const current = await requestToPromise(shifts.get(pointer.value));
            if (current && current.status === SHIFT_STATUS.OPEN) {
                await done;
                return { shift: current, created: false };
            }
        }

        const shift = {
            shift_id: newId(),
            terminal_id: terminal.terminal_id,
            store_id: terminal.store_id,
            status: SHIFT_STATUS.OPEN,
            opened_at: horodatageLocal(now()),
            opened_by: { employee_id: employee.employee_id, display_name: String(employee.display_name || '') },
            opening_float_cents: openingFloatCents,
            closed_at: null,
            closed_by: null,
        };
        shifts.add(shift);
        meta.put({ key: META_KEYS.OPEN_SHIFT, value: shift.shift_id });
        await done;
        return { shift, created: true };
    } catch (error) {
        done.catch(() => {});
        try { tx.abort(); } catch (_alreadyFinished) { /* transaction deja terminee */ }
        throw error;
    }
}
